import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { MdKeyboardArrowDown, MdOutlinePerson } from 'react-icons/md';
import {
  ChatContainer,
  MessagesWrapper,
  MessagesContainer,
  MessagesContent,
  LoadMoreTrigger,
  LoadMoreSpinner,
  EmptyStateSlot,
  MessageWrapper,
  MessageColumn,
  AIBadge,
  UserBadge,
  MessageBubble,
  MessageContent,
  MessageTime,
  ThinkingBubble,
  ThinkingDots,
  ThinkingDot,
  ThinkingLabel,
  InlineThinking,
  InlineThinkingDot,
  InlineThinkingLabel,
  ErrorWrapper,
  CompactingBanner,
  ToolChipSpinner,
  ErrorSupportText,
  JumpToBottomButton,
} from './Chat.styled';
import type { AskUserAnswer, Message, ModelSuggestion, PendingAskUser, SetupStep } from '@hooks/useChat';
import MessageBox, { MESSAGE_BOX_VARIANT_ERROR } from '@components/2-molecules/MessageBox';
import Markdown from './Markdown';
import AssistantContent from './AssistantContent';
import { isTrailStep } from './trailSteps';
import ModelSuggestionCard from './ModelSuggestionCard';
import ClarificationAnswersSummary from './AnswersSummary';
import GuardrailNotice from './GuardrailNotice';
import InterruptedNotice from './InterruptedNotice';
import { skillAccentColor, skillCategory } from '@constants/skillCategories';
import FileAttachments from './FileAttachments';
import ChatInputArea, { type ChatDraft } from './ChatInputArea';
import CreditPurchasePrompt from './CreditPurchasePrompt';
import ActionsBar from './ActionsBar';
import { extractSupportTrace, formatTime, getErrorMessageKey, messageTime, preserveScrollAfterLoad, turnStartedAt } from './helpers';

// ── Main component ─────────────────────────────────────────────────

interface ChatProps {
  messages: Message[];
  isLoading: boolean;
  /** True while reconnecting to an active SSE stream after a conversation switch */
  isReconnecting?: boolean;
  error: string | null;
  sendMessage: (message: string, files?: File[]) => Promise<void | boolean>;
  /** Cancel the in-flight AI request */
  onCancel?: () => void;
  onLoadMore?: () => void;
  hasMoreMessages?: boolean;
  isLoadingMore?: boolean;
  /** Pending model-swap suggestion awaiting accept/dismiss (AIB-7877) */
  pendingModelSuggestion?: ModelSuggestion | null;
  onAcceptModelSuggestion?: (runnerType?: string, approvalMode?: string) => void | Promise<void>;
  onDismissModelSuggestion?: (runnerType?: string, approvalMode?: string) => void | Promise<void>;
  /** Pending clarification batch awaiting answers (AIB-6745) */
  pendingAskUser?: PendingAskUser | null;
  onSubmitAskUser?: (answers: AskUserAnswer[], crops?: File[], runnerType?: string, approvalMode?: string) => void | Promise<void>;
  onSkipAskUser?: (message: string, files?: File[], runnerType?: string, approvalMode?: string) => void | Promise<void>;
  /** Tool approval callbacks */
  onApproveToolExecution?: (toolUseId: string) => Promise<void>;
  onRejectToolExecution?: (toolUseId: string, comment?: string) => Promise<void>;
  /** Message feedback callbacks */
  onSubmitFeedback?: (messageId: string, rating: 'up' | 'down', comment?: string, fullContent?: string) => Promise<void>;
  onDeleteFeedback?: (messageId: string) => Promise<void>;
  /** Retry an interrupted assistant turn: resend its user prompt, or `prompt` when given. */
  onRetryAfterInterruption?: (interruptedMessageId: string, prompt?: string) => Promise<void> | void;
  /** Transient pre-work steps (skill loads, environment boot) for the current turn */
  setupSteps?: SetupStep[];
  /** True while the setup block is animating out at the end of a turn */
  setupExiting?: boolean;
  /** True while the backend is compacting conversation history before this turn */
  compacting?: boolean;
  /** Pre-select a runner type in the input area (e.g. from the homepage picker) */
  initialRunnerType?: string;
  /** Conversation ID for persisting runner selection */
  conversationId?: string | null;
  /** Hide the runner / approval-mode / model pickers below the composer. */
  hideExecutionControls?: boolean;
  /** Overrides the composer placeholder, which the default outgrows in a narrow panel. */
  inputPlaceholder?: string;
  /** Shown in place of the transcript while the conversation has no messages. */
  emptyState?: React.ReactNode;
  /** Replaces what is in the composer, for the user to review and send. */
  draft?: ChatDraft;
}

const Chat: React.FC<ChatProps> = ({
  messages,
  isLoading,
  isReconnecting = false,
  error,
  sendMessage,
  onCancel,
  onLoadMore,
  hasMoreMessages = false,
  isLoadingMore = false,
  pendingModelSuggestion,
  onAcceptModelSuggestion,
  onDismissModelSuggestion,
  pendingAskUser,
  onSubmitAskUser,
  onSkipAskUser,
  onApproveToolExecution,
  onRejectToolExecution,
  onSubmitFeedback,
  onDeleteFeedback,
  onRetryAfterInterruption,
  setupSteps = [],
  setupExiting = false,
  compacting = false,
  initialRunnerType,
  conversationId,
  hideExecutionControls = false,
  inputPlaceholder,
  emptyState,
  draft,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const resizeScrollRafRef = useRef<number | null>(null);

  // True while the user is scrolled to (or near) the bottom of the message list.
  // Auto-scroll only fires when this is true so manually scrolling up stops the
  // viewport from snapping back down on every streaming chunk.
  const isAtBottomRef = useRef(true);
  const prevIsLoadingRef = useRef(false);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);
  
  const currentRunnerTypeRef = useRef<string>('cloud');
  const currentApprovalModeRef = useRef<string>('require_approval');
  const handleRunnerTypeChange = useCallback((runnerType: string) => {
    currentRunnerTypeRef.current = runnerType;
  }, []);
  const handleApprovalModeChange = useCallback((approvalMode: string) => {
    currentApprovalModeRef.current = approvalMode;
  }, []);

  const handleAcceptModelSuggestion = useCallback(() => {
    onAcceptModelSuggestion?.(currentRunnerTypeRef.current, currentApprovalModeRef.current);
  }, [onAcceptModelSuggestion]);

  const handleDismissModelSuggestion = useCallback(() => {
    onDismissModelSuggestion?.(currentRunnerTypeRef.current, currentApprovalModeRef.current);
  }, [onDismissModelSuggestion]);

  const scrollToBottom = useCallback((smooth = true) => {
    // Double rAF: first waits for React to commit the DOM, second for the
    // browser to finish layout so scrollHeight reflects the new content.
    // Used for explicit scrolls (navigation, new session, keyboard open/close).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const container = messagesContainerRef.current;
        if (!container) return;
        container.scrollTo({
          top: container.scrollHeight,
          behavior: smooth ? 'smooth' : 'instant',
        });
      });
    });
  }, []);

  // Derive thinking state:
  // - showThinking: full bubble when no assistant content has arrived yet
  // - showInlineThinking: lightweight "Thinking..." under the last assistant message
  // Suppress both when a tool is awaiting approval — the agent is blocked on user input.
  //
  // We only show inline thinking when the last message is an assistant message with
  // content. If the last message is a user message (e.g. optimistic update just landed
  // or the SSE stream hasn't produced any assistant content yet), we show the full
  // thinking bubble instead so it appears as a new turn rather than gluing dots onto
  // the previous assistant message.
  const lastMessage = messages.at(-1);
  const lastAssistantMessage = lastMessage?.role === 'assistant' ? lastMessage : null;
  const hasAssistantContent = lastAssistantMessage !== null && (
    lastAssistantMessage.content.length > 0
    || (lastAssistantMessage.toolExecutions?.length ?? 0) > 0
    || setupSteps.length > 0
  );
  const hasPendingApproval = messages.some((m) =>
    m.toolExecutions?.some((t) => t.status === 'pending_approval'),
  );
  const showThinking = isLoading && !isReconnecting && !compacting && !hasAssistantContent && !hasPendingApproval;
  // Once setup or steps have folded into a tool trail, its header carries the live status.
  const lastHasTrail = setupSteps.length > 0 || !!lastAssistantMessage?.toolExecutions?.some(isTrailStep);
  const showInlineThinking = isLoading && !isReconnecting && !compacting && hasAssistantContent && !hasPendingApproval && !lastHasTrail;

  // Keep isAtBottomRef in sync with the scroll position so we know whether the
  // user has manually scrolled away from the bottom.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const onScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = container;
      const atBottom = scrollTop + clientHeight >= scrollHeight - 200;
      isAtBottomRef.current = atBottom;
      setShowJumpToBottom(!atBottom);
    };

    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  // When the conversation changes, reset all scroll state and jump to the bottom.
  // isAtBottomRef may be false from the previous conversation (user had scrolled
  // up), which would otherwise suppress auto-scroll on the new conversation.
  // setShowJumpToBottom is intentionally omitted — it resets via the scroll
  // event that fires when scrollToBottom completes.
  useEffect(() => {
    isAtBottomRef.current = true;
    prevIsLoadingRef.current = false;
    scrollToBottom(false);
  }, [conversationId, scrollToBottom]);

  // When a new streaming session starts, snap back to the bottom and re-enable
  // auto-scroll regardless of where the user was previously.
  useEffect(() => {
    if (isLoading && !prevIsLoadingRef.current) {
      isAtBottomRef.current = true;
      scrollToBottom();
    }
    prevIsLoadingRef.current = isLoading;
  }, [isLoading, scrollToBottom]);

  // ResizeObserver is the single source of truth for content-based auto-scroll.
  // It fires whenever MessagesContent grows — streaming text, images loading,
  // 3D model canvases painting (Three.js setSize), error messages, etc.
  // We deduplicate via rAF: multiple resize events in the same frame (common
  // when Three.js resizes the canvas and React renders text simultaneously)
  // collapse into one scroll using the final settled scrollHeight.
  useEffect(() => {
    const content = messagesContentRef.current;
    if (!content) return;
    const observer = new ResizeObserver(() => {
      if (!isAtBottomRef.current || isLoadingMore) return;
      if (resizeScrollRafRef.current !== null) {
        cancelAnimationFrame(resizeScrollRafRef.current);
      }
      resizeScrollRafRef.current = requestAnimationFrame(() => {
        resizeScrollRafRef.current = null;
        const container = messagesContainerRef.current;
        if (container && isAtBottomRef.current) {
          container.scrollTop = container.scrollHeight;
        }
      });
    });
    observer.observe(content);
    return () => {
      observer.disconnect();
      if (resizeScrollRafRef.current !== null) {
        cancelAnimationFrame(resizeScrollRafRef.current);
        resizeScrollRafRef.current = null;
      }
    };
  }, [isLoadingMore]);

  // On mobile, the virtual keyboard opening/closing changes the container height,
  // which can break the at-bottom detection. Re-scroll when the visual viewport
  // resizes (keyboard open/close) if we were already at the bottom.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onViewportResize = () => {
      if (isAtBottomRef.current) {
        scrollToBottom(false);
      }
    };
    vv.addEventListener('resize', onViewportResize);
    return () => vv.removeEventListener('resize', onViewportResize);
  }, [scrollToBottom]);

  // Infinite scroll — load older messages when user scrolls near the top
  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    const container = messagesContainerRef.current;
    if (!trigger || !container || !onLoadMore || !hasMoreMessages) return;

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (!entries[0].isIntersecting || !hasMoreMessages || isLoadingMore) return;
      preserveScrollAfterLoad(container, container.scrollHeight, container.scrollTop);
      onLoadMore();
    };

    const observer = new IntersectionObserver(handleIntersect, {
      root: container,
      rootMargin: '200px 0px 0px 0px',
      threshold: 0,
    });

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [onLoadMore, hasMoreMessages, isLoadingMore]);

  return (
    <ChatContainer>
      {/* ── Messages ──────────────────────────────────────────── */}
      <MessagesWrapper>
      <MessagesContainer ref={messagesContainerRef}>
        <MessagesContent ref={messagesContentRef}>
          {/* Infinite scroll trigger — sits at the top of the list */}
          <LoadMoreTrigger ref={loadMoreTriggerRef}>
            {isLoadingMore && <LoadMoreSpinner />}
          </LoadMoreTrigger>

          {emptyState && messages.length === 0 && !isLoading && (
            <EmptyStateSlot>{emptyState}</EmptyStateSlot>
          )}

          {messages.map((msg, idx) => {
            // The setup block hangs off the last assistant message, which is still
            // empty while skills load and the environment boots — keep it mounted.
            const carriesSetup = idx === messages.length - 1 && setupSteps.length > 0;
            const trailProps = {
              active: isLoading && idx === messages.length - 1,
              turnStartedAt: turnStartedAt(messages, idx),
              setupSteps: carriesSetup ? setupSteps : undefined,
              setupExiting,
            };
            const turnFinished = !trailProps.active && !msg.isStreaming;
            if (
              msg.role === 'assistant'
              && !carriesSetup
              && !msg.content
              && !msg.toolExecutions?.length
              && !msg.inlineArtifacts?.length
              && !msg.agentBoundaries?.length
              && !msg.guardrailRetracted
              && !msg.interrupted
            ) {
              return null;
            }
            return (
            <MessageWrapper key={msg.clientId ?? msg.id} $role={msg.role}>
              {msg.role === 'user' && (
                <>
                  <MessageColumn $role="user">
                    <FileAttachments attachments={msg.attachments} role="user" />
                    {msg.askUserAnswers?.length ? (
                      <ClarificationAnswersSummary answers={msg.askUserAnswers} />
                    ) : (
                      <MessageBubble $role="user">
                        <Markdown>{msg.content}</Markdown>
                      </MessageBubble>
                    )}
                    <MessageTime>{formatTime(messageTime(msg))}</MessageTime>
                  </MessageColumn>
                  <UserBadge>
                    <MdOutlinePerson />
                  </UserBadge>
                </>
              )}

              {msg.role === 'assistant' && (
                <>
                  <AIBadge>
                    <img src="/img/icons/logo.svg" alt="Aibuild" />
                  </AIBadge>
                  <MessageColumn $role="assistant">
                    {msg.guardrailRetracted ? (
                      <GuardrailNotice />
                    ) : (
                      <>
                        <MessageContent>
                          <AssistantContent {...trailProps} message={msg} agentAccentColor={skillAccentColor} agentCategoryFn={skillCategory} onApproveToolExecution={onApproveToolExecution} onRejectToolExecution={onRejectToolExecution} />
                        </MessageContent>
                        {msg.insufficientCredits?.canManageBilling && (
                          <CreditPurchasePrompt hasActiveSubscription={msg.insufficientCredits.hasActiveSubscription} />
                        )}
                        {msg.modelSuggestion && (
                          <ModelSuggestionCard
                            suggestedModelLabel={msg.modelSuggestion.suggestedModelLabel}
                            canAct={!!pendingModelSuggestion && idx === messages.length - 1}
                            onAccept={handleAcceptModelSuggestion}
                            onDismiss={handleDismissModelSuggestion}
                          />
                        )}
                        {msg.interrupted && (
                          <InterruptedNotice
                            reason={msg.interruptReason}
                            onRetry={onRetryAfterInterruption
                              ? (prompt) => { Promise.resolve(onRetryAfterInterruption(msg.id, prompt)).catch(() => undefined); }
                              : undefined}
                          />
                        )}
                        {turnFinished && !msg.guardrailRetracted && !msg.interrupted && onSubmitFeedback && onDeleteFeedback && (
                          <ActionsBar
                            message={msg}
                            onSubmitFeedback={onSubmitFeedback}
                            onDeleteFeedback={onDeleteFeedback}
                          />
                        )}
                        {idx === messages.length - 1 && showInlineThinking && (
                          <InlineThinking>
                            <InlineThinkingDot />
                            <InlineThinkingDot />
                            <InlineThinkingDot />
                            <InlineThinkingLabel>
                              <FormattedMessage id="chat.thinking" defaultMessage="Thinking..." />
                            </InlineThinkingLabel>
                          </InlineThinking>
                        )}
                      </>
                    )}
                    {turnFinished && <MessageTime>{formatTime(messageTime(msg))}</MessageTime>}
                  </MessageColumn>
                </>
              )}
            </MessageWrapper>
            );
          })}

          {isReconnecting && (
            <MessageWrapper $role="assistant">
              <AIBadge>
                <img src="/img/icons/logo.svg" alt="Aibuild" />
              </AIBadge>
              <MessageColumn $role="assistant">
                <ThinkingBubble data-testid="reconnecting-indicator">
                  <ThinkingDots>
                    <ThinkingDot />
                    <ThinkingDot />
                    <ThinkingDot />
                  </ThinkingDots>
                  <ThinkingLabel>
                    <FormattedMessage id="chat.reconnecting" defaultMessage="Resuming..." />
                  </ThinkingLabel>
                </ThinkingBubble>
              </MessageColumn>
            </MessageWrapper>
          )}

          {compacting && (
            <MessageWrapper $role="assistant">
              <AIBadge>
                <img src="/img/icons/logo.svg" alt="Aibuild" />
              </AIBadge>
              <MessageColumn $role="assistant">
                <CompactingBanner data-testid="compacting-indicator">
                  <ToolChipSpinner />
                  <FormattedMessage
                    id="chat.compacting"
                    defaultMessage="Compacting conversation history to free up context..."
                  />
                </CompactingBanner>
              </MessageColumn>
            </MessageWrapper>
          )}

          {showThinking && (
            <MessageWrapper $role="assistant">
              <AIBadge>
                <img src="/img/icons/logo.svg" alt="Aibuild" />
              </AIBadge>
              <MessageColumn $role="assistant">
                <ThinkingBubble data-testid="thinking-indicator">
                  <ThinkingDots>
                    <ThinkingDot />
                    <ThinkingDot />
                    <ThinkingDot />
                  </ThinkingDots>
                  <ThinkingLabel>
                    <FormattedMessage id="chat.thinking" defaultMessage="Thinking..." />
                  </ThinkingLabel>
                </ThinkingBubble>
              </MessageColumn>
            </MessageWrapper>
          )}

          {error && (() => {
            const errorKey = getErrorMessageKey(error);
            const showSupportText = errorKey !== 'chat.error.conversationLimit';
            return (
              <ErrorWrapper>
                <MessageBox variant={MESSAGE_BOX_VARIANT_ERROR}>
                  <span>
                    <FormattedMessage
                      id={errorKey}
                      defaultMessage={error}
                      values={{ trace: extractSupportTrace(error) }}
                    />
                    {showSupportText && (
                      <ErrorSupportText>
                        <FormattedMessage id="chat.error.support" />
                      </ErrorSupportText>
                    )}
                  </span>
                </MessageBox>
              </ErrorWrapper>
            );
          })()}

          <div ref={messagesEndRef} />
        </MessagesContent>
      </MessagesContainer>

      {showJumpToBottom && (
        <JumpToBottomButton onClick={() => scrollToBottom(true)} aria-label="Jump to bottom">
          <MdKeyboardArrowDown />
        </JumpToBottomButton>
      )}
      </MessagesWrapper>

      {/* ── Input ─────────────────────────────────────────────── */}
      <ChatInputArea
        sendMessage={sendMessage}
        isLoading={isLoading}
        isInputDisabled={isReconnecting}
        onCancel={onCancel}
        initialRunnerType={initialRunnerType}
        conversationId={conversationId}
        onRunnerTypeChange={handleRunnerTypeChange}
        onApprovalModeChange={handleApprovalModeChange}
        pendingAskUser={pendingAskUser}
        onSubmitAskUser={onSubmitAskUser}
        onSkipAskUser={onSkipAskUser}
        hideExecutionControls={hideExecutionControls}
        inputPlaceholder={inputPlaceholder}
        draft={draft}
      />
    </ChatContainer>
  );
};

export default Chat;
